import { findDuplicateHeaders } from './column_hints';
import { letterToIndex, numberToIndex } from './keys';

/**
 * @param {{ reference: string, header?: string, letter?: string, number?: number }} col
 * @param {string} rowVar
 * @param {string} headersVar
 * @returns {string}
 */
function emitReadExpr(col, rowVar, headersVar) {
  const ref = col.reference || 'header';
  if (ref === 'letter') {
    const idx = letterToIndex(col.letter || 'A');
    return `${rowVar}[${idx}]`;
  }
  if (ref === 'number') {
    const idx = numberToIndex(col.number ?? 1);
    return `${rowVar}[${idx}]`;
  }
  const header = String(col.header || '').replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  return `(function(){ const i = ${headersVar}.indexOf("${header}"); return i >= 0 ? ${rowVar}[i] : ""; })()`;
}

/**
 * @param {import('./config_schema.js').PortalConfig} config
 * @param {'lookup'} role
 * @returns {string}
 */
function emitLookupCompare(config, rowVar, headersVar) {
  const lookup = config.sheet.lookup;
  const param = config.sheet.urlParam || 'id';
  const ref = lookup.reference || 'header';

  if (ref === 'letter') {
    const idx = letterToIndex(lookup.letter || 'A');
    return `String(${rowVar}[${idx}]).trim() === ${param}`;
  }
  if (ref === 'number') {
    const idx = numberToIndex(lookup.number ?? 1);
    return `String(${rowVar}[${idx}]).trim() === ${param}`;
  }
  const header = String(lookup.header || '').replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  return `(function(){ const i = ${headersVar}.indexOf("${header}"); return i >= 0 && String(${rowVar}[i]).trim() === ${param}; })()`;
}

/**
 * @param {import('./config_schema.js').PortalConfig} config
 * @param {{ includeBuilderComments?: boolean }} [options]
 * @returns {string}
 */
export function generateCodeGs(config, options = {}) {
  const { includeBuilderComments = false } = options;
  const sheetName = String(config.sheet.name || 'Sheet1').replace(/"/g, '\\"');
  const htmlFile = String(config.script.htmlFileName || 'Page').replace(/"/g, '\\"');
  const pageTitle = String(config.script.pageTitle || 'Portal').replace(/"/g, '\\"');
  const urlParam = (config.sheet.urlParam || 'id').replace(/[^\w]/g, '') || 'id';
  const placeholder = config.script.webAppUrlPlaceholder || 'PASTE_DEPLOYMENT_URL_HERE';
  const allowAll = config.script.allowAllFrames !== false;
  const renewalDays = config.script.renewalDays ?? 14;

  const dupes = findDuplicateHeaders(config.sheet.columns);
  const lookupHeader = config.sheet.lookup?.header;
  const headerDupeComment =
    dupes.size > 0 && config.sheet.lookup?.reference === 'header' && lookupHeader && dupes.has(lookupHeader)
      ? `// Note: lookup header "${lookupHeader}" appears more than once. indexOf uses the first column only.\n`
      : '';

  const lines = [];
  lines.push(`const WEBAPP_URL = "${placeholder}";`);
  lines.push('');
  lines.push('function doGet(e) {');
  lines.push(`  const ${urlParam} = (e.parameter.${urlParam} || "").trim();`);
  lines.push(`  const sheet = SpreadsheetApp.getActiveSpreadsheet().getSheetByName("${sheetName}");`);
  lines.push('  if (!sheet) {');
  lines.push('    return HtmlService.createHtmlOutput("Sheet not found: ' + sheetName + '");');
  lines.push('  }');
  lines.push('  const data = sheet.getDataRange().getValues();');
  lines.push('  const headers = data[0].map(function(h) { return String(h).trim(); });');
  lines.push('');

  if (config.features?.shipmentTracker) {
    lines.push(...emitShipmentTrackerDoGet(config, urlParam, headerDupeComment));
  } else if (config.features?.multiRowLookup) {
    lines.push(...emitLibraryDoGet(config, urlParam, headerDupeComment));
  } else {
    lines.push(...emitSingleRowDoGet(config, urlParam, headerDupeComment));
  }

  lines.push('');
  lines.push(`  const template = HtmlService.createTemplateFromFile("${htmlFile}");`);
  lines.push('  template.data = result;');
  lines.push('  let output = template.evaluate().setTitle("' + pageTitle + '");');
  if (allowAll) {
    lines.push('  output = output.setXFrameOptionsMode(HtmlService.XFrameOptionsMode.ALLOWALL);');
  }
  lines.push('  return output;');
  lines.push('}');

  if (config.features?.doPostRenew) {
    lines.push('');
    lines.push(...emitDoPostRenew(config, sheetName, urlParam, renewalDays));
  }

  if (includeBuilderComments) {
    lines.push('');
    lines.push('// Deploy: use Portal Builder deploy guide at https://bettersheets.co/portal/builder');
    lines.push('// After editing this file: Deploy → Manage deployments → Edit → New version');
  }

  return lines.join('\n');
}

/**
 * @param {import('./config_schema.js').PortalConfig} config
 * @param {string} urlParam
 * @param {string} headerDupeComment
 */
function emitSingleRowDoGet(config, urlParam, headerDupeComment) {
  const lines = [];
  const notFoundMsg = String(config.html?.sections?.notFound?.message || '').replace(/\\/g, '\\\\').replace(/"/g, '\\"');

  lines.push(headerDupeComment + '  let result = {');
  lines.push('    found: false,');
  for (const col of config.sheet.columns) {
    if (col.key === 'status' && notFoundMsg) {
      lines.push(`    status: "${notFoundMsg}",`);
    } else {
      lines.push(`    ${col.key}: "",`);
    }
  }
  lines.push(`    ${urlParam}Value: "",`);
  lines.push('    webappUrl: WEBAPP_URL + "?' + urlParam + '=",');
  lines.push('    postUrl: WEBAPP_URL');
  lines.push('  };');
  lines.push('');
  lines.push('  for (let i = 1; i < data.length; i++) {');
  lines.push('    const row = data[i];');
  lines.push(`    if (${emitLookupCompare(config, 'row', 'headers')}) {`);
  lines.push('      result.found = true;');
  lines.push(`      result.${urlParam}Value = ${urlParam};`);
  for (const col of config.sheet.columns) {
    lines.push(`      result.${col.key} = ${emitReadExpr(col, 'row', 'headers')};`);
  }
  lines.push('      break;');
  lines.push('    }');
  lines.push('  }');
  return lines;
}

/**
 * Shipping Tracker: lookup by confirmation (col A), queue vs shipped display logic.
 * @param {import('./config_schema.js').PortalConfig} config
 * @param {string} urlParam
 * @param {string} headerDupeComment
 */
function emitShipmentTrackerDoGet(config, urlParam, headerDupeComment) {
  const st = config.html?.sections?.shippingTracker || {};
  const queueStatus = String(st.queueStatus || 'IN SHIP QUEUE').replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  const shippedLabel = String(st.shippedStatusLabel || 'SHIPPED').replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  const notFoundMsg = String(config.html?.sections?.notFound?.message || 'Tracking number not found')
    .replace(/\\/g, '\\\\')
    .replace(/"/g, '\\"');
  const lookupCol =
    config.sheet.columns.find((c) => c.header === config.sheet.lookup?.header) ||
    config.sheet.columns.find((c) => c.letter === config.sheet.lookup?.letter) ||
    config.sheet.columns[0];
  const lookupKey = lookupCol?.key || 'confirmationNo';

  const lines = [];
  lines.push(headerDupeComment + '  let result = {');
  lines.push('    found: false,');
  lines.push(`    ${urlParam}: ${urlParam},`);
  lines.push(`    status: "${notFoundMsg}",`);
  lines.push('    inQueue: false,');
  lines.push('    displayStatus: "",');
  lines.push('    displayTrackingNo: "",');
  lines.push('    displayTrackingLink: "",');
  lines.push('    searchNumber: "",');
  for (const col of config.sheet.columns) {
    lines.push(`    ${col.key}: "",`);
  }
  lines.push('    webappUrl: WEBAPP_URL + "?' + urlParam + '=",');
  lines.push('    postUrl: WEBAPP_URL');
  lines.push('  };');
  lines.push('');
  lines.push('  for (let i = 1; i < data.length; i++) {');
  lines.push('    const row = data[i];');
  lines.push(`    if (${emitLookupCompare(config, 'row', 'headers')}) {`);
  lines.push('      result.found = true;');
  for (const col of config.sheet.columns) {
    lines.push(`      result.${col.key} = ${emitReadExpr(col, 'row', 'headers')};`);
  }
  lines.push(`      result.searchNumber = String(result.${lookupKey} || "").trim();`);
  lines.push('      const statusRaw = String(result.status || "").trim();');
  lines.push(`      const queueStatus = "${queueStatus}";`);
  lines.push('      if (statusRaw === queueStatus) {');
  lines.push('        result.inQueue = true;');
  lines.push('        result.displayStatus = statusRaw;');
  lines.push('        result.displayTrackingNo = statusRaw;');
  lines.push('        result.displayTrackingLink = "";');
  lines.push('      } else {');
  lines.push('        result.inQueue = false;');
  lines.push(`        result.displayStatus = "${shippedLabel}";`);
  lines.push('        result.displayTrackingNo = String(result.trackingNo || "").trim();');
  lines.push('        result.displayTrackingLink = String(result.trackingLink || "").trim();');
  lines.push('      }');
  lines.push('      break;');
  lines.push('    }');
  lines.push('  }');
  return lines;
}

function emitLibraryDoGet(config, urlParam, headerDupeComment) {
  const loanFields = [
    { key: 'checkoutId', header: 'Checkout_ID' },
    { key: 'patronId', header: 'Patron_ID' },
    { key: 'patronName', header: 'Patron_Name' },
    { key: 'bookTitle', header: 'Book_Title' },
    { key: 'author', header: 'Author' },
    { key: 'status', header: 'Status' },
    { key: 'dueDate', header: 'Due_Date' },
    { key: 'checkedOutDate', header: 'Checked_Out_Date' },
    { key: 'renewalCount', header: 'Renewal_Count' },
    { key: 'shelfLocation', header: 'Shelf_Location' },
    { key: 'notes', header: 'Notes' }
  ];

  const lines = [];
  lines.push(headerDupeComment + '  const loans = [];');
  lines.push('  let result = {');
  lines.push('    found: false,');
  lines.push('    loans: loans,');
  lines.push(`    patronId: ${urlParam},`);
  lines.push('    webappUrl: WEBAPP_URL + "?' + urlParam + '=",');
  lines.push('    postUrl: WEBAPP_URL');
  lines.push('  };');
  lines.push('');
  lines.push('  for (let i = 1; i < data.length; i++) {');
  lines.push('    const row = data[i];');
  lines.push(`    if (${emitLookupCompare(config, 'row', 'headers')}) {`);
  lines.push('      loans.push({');
  for (const f of loanFields) {
    const col = config.sheet.columns.find((c) => c.header === f.header) || {
      header: f.header,
      reference: 'header',
      letter: 'A',
      number: 1
    };
    lines.push(`        ${f.key}: ${emitReadExpr(col, 'row', 'headers')},`);
  }
  lines.push('      });');
  lines.push('    }');
  lines.push('  }');
  lines.push('  result.found = loans.length > 0;');
  return lines;
}

function emitDoPostRenew(config, sheetName, urlParam, renewalDays) {
  const checkoutHeader = 'Checkout_ID';
  const dueHeader = 'Due_Date';
  const renewHeader = 'Renewal_Count';

  return [
    'function doPost(e) {',
    '  const action = (e.parameter.action || "").trim();',
    '  if (action !== "renew") {',
    '    return HtmlService.createHtmlOutput("Unknown action");',
    '  }',
    `  const checkoutId = (e.parameter.checkoutId || "").trim();`,
    `  const patron = (e.parameter.${urlParam} || "").trim();`,
    `  const sheet = SpreadsheetApp.getActiveSpreadsheet().getSheetByName("${sheetName.replace(/"/g, '\\"')}");`,
    '  if (!sheet) {',
    '    return HtmlService.createHtmlOutput("Sheet not found");',
    '  }',
    '  const data = sheet.getDataRange().getValues();',
    '  const headers = data[0].map(function(h) { return String(h).trim(); });',
    `  const idCol = headers.indexOf("${checkoutHeader}");`,
    `  const dueCol = headers.indexOf("${dueHeader}");`,
    `  const renewCol = headers.indexOf("${renewHeader}");`,
    '  if (idCol < 0 || dueCol < 0) {',
    '    return HtmlService.createHtmlOutput("Missing Checkout_ID or Due_Date column");',
    '  }',
    '  for (let i = 1; i < data.length; i++) {',
    '    if (String(data[i][idCol]).trim() === checkoutId) {',
    '      const newDue = new Date();',
    `      newDue.setDate(newDue.getDate() + ${renewalDays});`,
    '      sheet.getRange(i + 1, dueCol + 1).setValue(newDue);',
    '      if (renewCol >= 0) {',
    '        const curr = data[i][renewCol];',
    '        const next = (Number(curr) || 0) + 1;',
    '        sheet.getRange(i + 1, renewCol + 1).setValue(next);',
    '      }',
    '      break;',
    '    }',
    '  }',
    `  const redirect = WEBAPP_URL + "?${urlParam}=" + encodeURIComponent(patron);`,
    '  return HtmlService.createHtmlOutput(\'<script>top.location.href="\' + redirect + \'";</script>\');',
    '}'
  ];
}
